import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRACTICE_AREAS } from "@/data/practiceAreas";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export const PracticeAreasSection = () => {
  return (
    <section
      id="practice-areas"
      data-testid="practice-areas-section"
      className="bg-canvas py-16 md:py-20"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                Practice Areas
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-charcoal leading-tight max-w-2xl">
              Counsel across the full corporate lifecycle.
            </h2>
          </div>
          <p className="font-serif text-sm md:text-base text-charcoal/60 max-w-sm leading-relaxed">
            Ten focused practices spanning the UAE, Middle East, and the Africa corridor.
          </p>
        </div>

        {/* Tiles grid: 2 cols mobile, 5 cols desktop */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "0px 0px -80px 0px" }}
          className="grid grid-cols-2 lg:grid-cols-5 border-t border-l border-charcoal/12"
        >
          {PRACTICE_AREAS.map((area) => {
            const Icon = area.icon;
            return (
              <motion.div key={area.slug} variants={item}>
                <Link
                  to={`/practice/${area.slug}`}
                  data-testid={`practice-tile-${area.slug}`}
                  className="group relative flex flex-col justify-between h-full min-h-[172px] p-5 lg:p-5 border-r border-b border-charcoal/12 bg-canvas hover:bg-forest transition-colors duration-400"
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex items-center justify-center w-9 h-9 border border-charcoal/15 text-forest group-hover:text-gold group-hover:border-canvas/30 transition-colors duration-400">
                      <Icon className="w-4 h-4" strokeWidth={1.4} />
                    </span>
                    <span className="font-sans text-[10px] tracking-[0.2em] text-charcoal/30 group-hover:text-canvas/40 transition-colors duration-400">
                      {area.index}
                    </span>
                  </div>

                  <div className="mt-6">
                    <h3 className="font-serif text-base lg:text-lg text-charcoal group-hover:text-canvas leading-snug transition-colors duration-400">
                      {area.title}
                    </h3>
                    <div className="mt-2.5 flex items-center gap-2 overflow-hidden">
                      <p className="font-sans text-[11px] tracking-wide text-charcoal/45 group-hover:text-canvas/60 transition-colors duration-400">
                        {area.description}
                      </p>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.16em] uppercase text-forest opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-gold transition-all duration-400">
                      View
                      <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
